import { handleize } from '../../core/helpers';

export default class DynamicProductExtraV4 {
  selected_color_name;
  selected_color_handle;
  quickview;
  prefix;
  is_plus;
  extra_data;
  style_extra_data_default;

  /**
   * Constructor
   * @param {string} selected_color_name - Selected color name
   * @param {boolean} quickview - Whether it's a quickview - Not used at the moment
   */
  constructor(selected_color_name, quickview = false) {
    this.selected_color_name = selected_color_name;
    this.selected_color_handle = handleize(selected_color_name);
    this.quickview = quickview;
    this.prefix = (quickview) ? '-quickview' : '';
    this.is_plus = selected_color_name.includes('Plus Size - ');
    this.extra_data = this.getExtraData() || null;
    this.style_extra_data_default = this.extra_data.find(obj => obj.color === 'default');

    this.updateOtherFields();
    this.updateDescription();
    this.updateWhyLove();
    this.updateSizeFit();
    this.initListeners();
  }

  initListeners() {
    document.addEventListener('click', (e) => {
      const tabTrigger = e.target.closest('[data-open-overview-tab]');
      if (tabTrigger) {
        this.openAccordionItem(tabTrigger.dataset.openOverviewTab);
      }
    });
  }

  /**
   * Get extra data from variant JSON
   * @returns {Object|null}
   */
  getExtraData() {
    try {
      const variant_json_el = document.querySelector(`[data-product-extra-variant-json]`);
      if (!variant_json_el) {
        return null;
      }

      return JSON.parse(variant_json_el.textContent);
    } catch (err) {
      console.error('Failed to parse product extra variant JSON:', err);
      return null;
    }
  }

  getStyleExtraData() {
    let style_extra_data = this.style_extra_data_default;

    if (this.selected_color_name !== '') {
      style_extra_data = this.extra_data.find(obj => obj.color === this.selected_color_handle);
    }

    if (typeof style_extra_data === 'undefined') {
      style_extra_data = this.style_extra_data_default;
    }

    return style_extra_data;
  }

  updateOtherFields() {
    const note_text = this.is_plus ? 'Size 1X:' : 'Size Small:';
    const selector = this.quickview ? 
      '#quicklook-modal .product-extra-v4__sizefit__garment-mesurements__note' : 
      '[data-product-overview] .product-extra-v4__sizefit__garment-mesurements__note';
      
    const title_element = document.querySelector(selector);
    if (title_element) {
      title_element.innerHTML = note_text;
    }

    return;
  }

  updateDescription() {
    // Some fields can be null on some products (gift cards...)
    this.style_extra_data_default.description = this.style_extra_data_default.description || '';
    this.style_extra_data_default.description_bullets = this.style_extra_data_default.description_bullets || '';

    const style_extra_data = this.getStyleExtraData();

    let extra_description = '';
    const extra_desc_el = document.querySelector(`[data${this.prefix}-extra-description]`);
    if (extra_desc_el) {
      extra_description = (style_extra_data.description && style_extra_data.description.length && style_extra_data.description !== '<span class="metafield-string"></span>') ? style_extra_data.description : this.style_extra_data_default.description;
      extra_desc_el.innerHTML = extra_description;

      (extra_description === null || extra_description === '') ? extra_desc_el.classList.add('visually-hidden') : extra_desc_el.classList.remove('visually-hidden');
    }

    let extra_description_bullets = '';
    const extra_desc_bullets_el = document.querySelector(`[data${this.prefix}-extra-description-bullets]`);
    if (extra_desc_bullets_el) {
      extra_description_bullets = (style_extra_data.description_bullets && style_extra_data.description_bullets.length && style_extra_data.description_bullets !== '<span class="metafield-string"></span>') ? style_extra_data.description_bullets : this.style_extra_data_default.description_bullets;
      extra_desc_bullets_el.innerHTML = extra_description_bullets;

      (extra_description_bullets === null || extra_description_bullets === '' || extra_description_bullets === '<span class="metafield-string"></span>') ? extra_desc_bullets_el.closest('.accordion__item').classList.add('visually-hidden') : extra_desc_bullets_el.closest('.accordion__item').classList.remove('visually-hidden');
    }
  }

  updateWhyLove() {
    // Some fields can be null on some products (gift cards...)
    this.style_extra_data_default.whylove_design = this.style_extra_data_default.whylove_design || '';
    this.style_extra_data_default.whylove_fit = this.style_extra_data_default.whylove_fit || '';
    this.style_extra_data_default.whylove_occasion = this.style_extra_data_default.whylove_occasion || '';

    const style_extra_data = this.getStyleExtraData();

    // Update all whylove fields dynamically
    const whyloveFields = ['design', 'fit', 'occasion'];
    
    let all_whylove_empty = true;
    whyloveFields.forEach(field => {
      const fieldKey = `whylove_${field}`;
      const element = document.querySelector(`[data${this.prefix}-extra-whylove-${field}]`);
      
      if (element) {
        const content = (style_extra_data[fieldKey] && style_extra_data[fieldKey].length && style_extra_data[fieldKey] !== '<span class="metafield-string"></span>') 
          ? style_extra_data[fieldKey] 
          : this.style_extra_data_default[fieldKey];
        
        element.innerHTML = content;

        // Toggle visibility based on content
        const parentItem = element.closest('.product-extra-v4__whylove');
        if (parentItem) {
          parentItem.classList.add('visually-hidden');
          if (content && content !== '' && content !== '<span class="metafield-string"></span>') {
            parentItem.classList.remove('visually-hidden');
            all_whylove_empty = false;
          }
        }
      }
    });

    document.querySelector(`[data${this.prefix}-extra-whylove]`)?.classList.toggle('visually-hidden', all_whylove_empty);
  }

  updateSizeFit() {
    // Some fields can be null on some products (gift cards...)
    this.style_extra_data_default.sizefit_height = this.style_extra_data_default.sizefit_height || '';
    this.style_extra_data_default.sizefit_size = this.style_extra_data_default.sizefit_size || '';
    this.style_extra_data_default.sizefit_hip = this.style_extra_data_default.sizefit_hip || '';
    this.style_extra_data_default.sizefit_waist = this.style_extra_data_default.sizefit_waist || '';
    this.style_extra_data_default.sizefit_bust = this.style_extra_data_default.sizefit_bust || '';
    this.style_extra_data_default.sizefit_fit = this.style_extra_data_default.sizefit_fit || '';
    this.style_extra_data_default.sizefit_length = this.style_extra_data_default.sizefit_length || '';
    this.style_extra_data_default.sizefit_inseam = this.style_extra_data_default.sizefit_inseam || '';
    this.style_extra_data_default.sizefit_sizing_recommendation = this.style_extra_data_default.sizefit_sizing_recommendation || '';

    const style_extra_data = this.getStyleExtraData();

    // Update all sizefit fields dynamically
    const sizeFitFields = ['height', 'size', 'hip', 'waist', 'bust', 'fit', 'length', 'inseam', 'sizing_recommendation'];
    
    sizeFitFields.forEach(field => {
      const fieldKey = `sizefit_${field}`;
      const elements = document.querySelectorAll(`[data${this.prefix}-extra-sizefit-${field}]`);
      
      if (elements.length) {
        const content = (style_extra_data[fieldKey] && style_extra_data[fieldKey].length && style_extra_data[fieldKey] !== '<span class="metafield-string"></span>') 
          ? style_extra_data[fieldKey] 
          : this.style_extra_data_default[fieldKey];
        
        elements.forEach(el =>  {
          el.innerHTML = content;
          
          // Toggle visibility based on content
          const parentItem = el.closest('.product-extra-v4__sizefit');
          if (parentItem) {
            parentItem.classList.toggle('visually-hidden', !content || content === '' || content === '<span class="metafield-string"></span>');
          }

          // Content on top of 2nd desktop image
          const parentItemImage = el.closest('.product-main-info__item--model');
          if (parentItemImage) {
            parentItemImage.classList.toggle('visually-hidden', !content || content === '' || content === '<span class="metafield-string"></span>');
          }

          // Content below size selection
          const parentItemSizesExtra = el.closest(`.product-option__sizes-extra [data${this.prefix}-extra-sizefit-${field}]`)?.parentElement;
          if (parentItemSizesExtra) {
            parentItemSizesExtra.classList.toggle('visually-hidden', !content || content === '' || content === '<span class="metafield-string"></span>');
          }
        });
      }
    });

    this.style_extra_data_default.fitinfo_bust = this.style_extra_data_default.fitinfo_bust || '';
    this.style_extra_data_default.fitinfo_waist = this.style_extra_data_default.fitinfo_waist || '';
    this.style_extra_data_default.fitinfo_hips = this.style_extra_data_default.fitinfo_hips || '';
    this.style_extra_data_default.fitinfo_undergarments = this.style_extra_data_default.fitinfo_undergarments || '';
    this.style_extra_data_default.fitinfo_stretch = this.style_extra_data_default.fitinfo_stretch || '';
    const fitInfoFields = ['bust', 'waist', 'hips', 'undergarments', 'stretch'];
    
    fitInfoFields.forEach(field => {
      const fieldKey = `fitinfo_${field}`;
      const elements = document.querySelectorAll(`[data${this.prefix}-extra-fitinfo-${field}]`);
      
      if (elements.length) {
        const content = (style_extra_data[fieldKey] && style_extra_data[fieldKey].length && style_extra_data[fieldKey] !== '<span class="metafield-string"></span>') 
          ? style_extra_data[fieldKey] 
          : this.style_extra_data_default[fieldKey];
        
        elements.forEach(el =>  {
          el.innerHTML = content;
          
          // Toggle visibility based on content
          const parentItem = el.closest('.product-extra-v4__sizefit');
          if (parentItem) {
            parentItem.classList.toggle('visually-hidden', !content || content === '' || content === '<span class="metafield-string"></span>');
          }
        });
      }
    }); 
  }

  openAccordionItem(itemName) {
    const accordion = document.querySelector(`.product-extra-v4__accordion`);
    if (!accordion) {
      return;
    }

    const target = accordion.querySelector(`[data-${itemName}]`);
    if (!target) {
      return;
    }

    if (!target.classList.contains('active')) {
      target.querySelector('.accordion__header').click();
    }
    
    target.scrollIntoView({ behavior: 'smooth' });
  }
}
